"use client";

import { useEffect, useMemo, useRef } from "react";
import { Table2 } from "lucide-react";

import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAllDataTables } from "@/hooks/use-all-data-tables";
import { cn } from "@/lib/utils";
import {
  resolveDataTableAccessMode,
  toDataTableIntegrationPayload,
} from "./constants";
import { DataTableAccessModeSelector } from "./DataTableAccessModeSelector";
import { DataTableAvailableOptions } from "./DataTableAvailableOptions";

interface ConnectedDataTableIntegrationsProps {
  integrations: DataTableIntegration[];
  savedIntegrationIds?: string[];
  disabled?: boolean;
  onChange?: (integrations: DataTableIntegration[]) => void;
  focusedDataTableId?: string | null;
  onFocusComplete?: () => void;
  variant?: "detailed" | "compact";
  className?: string;
}

interface DataTableIntegrationCardProps {
  integration: DataTableIntegration;
  tableName: string;
  tableDescription?: string;
  disabled: boolean;
  highlight?: boolean;
  variant: "detailed" | "compact";
  onAccessModeChange: (mode: DataTableAccessMode) => void;
  onUsageInstructionsChange: (value: string) => void;
}

function scrollToIntegrationCard(dataTableId: string) {
  const container = document.querySelector("[data-tools-scroll-container]");
  const card = document.querySelector(
    `[data-data-table-id="${dataTableId}"]`
  );

  if (!card) {
    return;
  }

  if (container instanceof HTMLElement) {
    const containerRect = container.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const scrollTop =
      container.scrollTop + (cardRect.top - containerRect.top) - 16;

    container.scrollTo({ top: Math.max(scrollTop, 0), behavior: "smooth" });
    return;
  }

  card.scrollIntoView({ behavior: "smooth", block: "start" });
}

function DataTableIntegrationCard({
  integration,
  tableName,
  tableDescription,
  disabled,
  highlight = false,
  variant,
  onAccessModeChange,
  onUsageInstructionsChange,
}: DataTableIntegrationCardProps) {
  const accessMode = resolveDataTableAccessMode(integration.access_mode);

  return (
    <div
      data-data-table-id={integration.data_table_id}
      className={cn(
        "space-y-4 rounded-xl border bg-card p-4",
        highlight && "border-primary ring-1 ring-primary/20"
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-6 w-6 shrink-0 items-center justify-center text-muted-foreground">
          <Table2 className="h-6 w-6" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-base font-medium">{tableName}</h4>
          {tableDescription?.trim() ? (
            <p className="text-sm text-muted-foreground">{tableDescription}</p>
          ) : null}
        </div>
      </div>

      {variant === "compact" ? (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(260px,0.8fr)]">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Access mode</Label>
              <DataTableAccessModeSelector
                value={accessMode}
                onChange={onAccessModeChange}
                disabled={disabled}
                compact
                idPrefix={`access-mode-${integration.data_table_id}`}
              />
            </div>

            <DataTableAvailableOptions accessMode={accessMode} />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor={`usage-${integration.data_table_id}`}
              className="text-sm font-medium"
            >
              Usage instructions
            </Label>
            <Textarea
              id={`usage-${integration.data_table_id}`}
              value={integration.usage_instructions}
              onChange={(e) => onUsageInstructionsChange(e.target.value)}
              placeholder="Provide instructions for the AI on how and when to use this data table."
              rows={3}
              disabled={disabled}
              className="resize-none text-sm"
            />
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Access mode</Label>
            <DataTableAccessModeSelector
              value={accessMode}
              onChange={onAccessModeChange}
              disabled={disabled}
              idPrefix={`access-mode-${integration.data_table_id}`}
            />
          </div>

          <DataTableAvailableOptions accessMode={accessMode} />

          <div className="space-y-2">
            <Label
              htmlFor={`usage-${integration.data_table_id}`}
              className="text-sm font-medium"
            >
              Usage instructions
            </Label>
            <Textarea
              id={`usage-${integration.data_table_id}`}
              value={integration.usage_instructions}
              onChange={(e) => onUsageInstructionsChange(e.target.value)}
              placeholder="Provide instructions for the AI on how and when to use this data table."
              rows={3}
              disabled={disabled}
              className="resize-none text-sm"
            />
          </div>
        </>
      )}
    </div>
  );
}

export function ConnectedDataTableIntegrations({
  integrations,
  savedIntegrationIds = [],
  disabled = false,
  onChange,
  focusedDataTableId,
  onFocusComplete,
  variant = "detailed",
  className,
}: ConnectedDataTableIntegrationsProps) {
  const { data: tables = [] } = useAllDataTables();
  const pendingSectionRef = useRef<HTMLDivElement>(null);

  const savedIdSet = useMemo(
    () => new Set(savedIntegrationIds),
    [savedIntegrationIds]
  );

  const pendingIntegrations = useMemo(
    () =>
      integrations.filter(
        (integration) => !savedIdSet.has(integration.data_table_id)
      ),
    [integrations, savedIdSet]
  );

  const savedIntegrations = useMemo(
    () =>
      integrations.filter((integration) =>
        savedIdSet.has(integration.data_table_id)
      ),
    [integrations, savedIdSet]
  );

  useEffect(() => {
    if (!focusedDataTableId) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      scrollToIntegrationCard(focusedDataTableId);
      onFocusComplete?.();
    });

    return () => cancelAnimationFrame(frameId);
  }, [focusedDataTableId, onFocusComplete]);

  if (!integrations.length) {
    return null;
  }

  const getTable = (dataTableId: string) =>
    tables.find((table) => table.id === dataTableId);

  const handleIntegrationUpdate = (updated: DataTableIntegration) => {
    onChange?.(
      integrations.map((integration) =>
        integration.data_table_id === updated.data_table_id
          ? toDataTableIntegrationPayload(updated)
          : toDataTableIntegrationPayload(integration)
      )
    );
  };

  const renderIntegrationCard = (
    integration: DataTableIntegration,
    options: { highlight?: boolean }
  ) => {
    const table = getTable(integration.data_table_id);

    return (
      <DataTableIntegrationCard
        key={integration.data_table_id}
        integration={integration}
        tableName={table?.name ?? "Data table"}
        tableDescription={table?.description ?? undefined}
        disabled={disabled}
        highlight={options.highlight}
        variant={variant}
        onAccessModeChange={(mode) =>
          handleIntegrationUpdate({
            ...integration,
            access_mode: mode,
          })
        }
        onUsageInstructionsChange={(value) =>
          handleIntegrationUpdate({
            ...integration,
            usage_instructions: value,
          })
        }
      />
    );
  };

  return (
    <div
      className={cn(
        variant === "compact" ? "space-y-5" : "space-y-6",
        className,
      )}
    >
      {pendingIntegrations.length > 0 ? (
        <div ref={pendingSectionRef} className="space-y-4">
          <div
            className={cn(
              "text-sm font-medium",
              variant === "compact" ? "text-foreground" : "text-muted-foreground",
            )}
          >
            Configure new data table
          </div>
          <div
            className={cn(
              "grid grid-cols-1",
              variant === "compact" ? "gap-4" : "gap-6",
            )}
          >
            {pendingIntegrations.map((integration) =>
              renderIntegrationCard(integration, {
                highlight: integration.data_table_id === focusedDataTableId,
              })
            )}
          </div>
        </div>
      ) : null}

      {savedIntegrations.length > 0 ? (
        <div className="space-y-4">
          <div
            className={cn(
              "text-sm font-medium",
              variant === "compact" ? "text-foreground" : "text-muted-foreground",
            )}
          >
            Access for Connected Data Tables
          </div>
          <div
            className={cn(
              "grid grid-cols-1",
              variant === "compact" ? "gap-4" : "gap-6",
            )}
          >
            {savedIntegrations.map((integration) =>
              renderIntegrationCard(integration, {})
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
